import React from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useFormContext, Controller } from "react-hook-form";
import states from "states-us";

import SubVendorAdditionalInfoFields from "./SubVendorAdditionalInfoFields";
import StatesCheckboxInput from "./StatesCheckboxInput";

import {
    getFormattedPhoneNumber,
    isValidEmail,
    isValidHttpUrl,
    isValidName,
    isValidPhoneNumber,
    isValidZipCode,
    isWebsiteValid,
} from "../utils/utils";
import {
    poolRoles,
    payrollCategories,
    regularRadioAnswer,
    smallBusinessCategories,
    nursingPoolRoles,
    discoverySources,
} from "../utils/subVendorsFormCommon";

const RadioYesAndNoQuestionField = ({ error, fieldName, onChange, value }) => {
    return (
        <>
            {regularRadioAnswer.map(({ label, value: radioValue }) => (
                <Form.Check
                    key={`radio_${fieldName}_${label}`}
                    type="radio"
                    id={`radio_${fieldName}_${label}`}
                    label={label}
                    value={radioValue}
                    checked={value === radioValue}
                    onChange={() => onChange(radioValue)}
                    isInvalid={!!error}
                />
            ))}

            <Form.Control.Feedback type="invalid" className="d-block">
                {error?.message}
            </Form.Control.Feedback>
        </>
    );
};

const SubVendorFirstFields = () => {
    const {
        control,
        register,
        formState: { errors },
        setValue,
        watch,
    } = useFormContext();

    const requiredFieldMessage = "This field is required.";
    const invalidFieldMessage = "This field is not valid.";
    const radioYesAndNoValidation = {
        validate: value => value !== undefined || "This field is required",
    };

    const updatePhoneNumber = (e, field) => {
        const number = getFormattedPhoneNumber(e.target.value);
        setValue(field, number);
    };

    const addHttps = websiteUrl => {
        if (!websiteUrl) return;

        if (isValidHttpUrl(websiteUrl)) return;

        setValue("company_website", `https://${websiteUrl}`);
    };

    const hasEOR = watch("has_eor");
    const hasPEO = watch("has_peo");
    const hasBackOffice = watch("has_back_office_organization");
    const excludedByCareerstaff = watch("excluded_by_careerstaff");
    const excludedByOIG = watch("excluded_by_oig");
    const isSmallBusiness = watch("is_small_business");
    const staffsLocally = watch("staffs_locally");
    const staffsTravel = watch("staffs_travel");
    const staffsPerDiem = watch("staffs_per_diem");
    const staffsContracts = watch("staffs_contracts");
    const candidatePoolRoles = watch("candidate_pool_roles");
    const careerstaffDiscoverySources = watch("careerstaff_discovery_sources");

    return (
        <Row as="fieldset">
            <Form.Group as={Col} md={6} className="mb-4">
                <Form.Label>
                    Name of Staffing Company{" "}
                    <span className="required-label">*</span>
                </Form.Label>
                <Form.Control
                    type="text"
                    size="lg"
                    {...register("company_name", {
                        required: requiredFieldMessage,
                        validate: value =>
                            isValidName(value) || invalidFieldMessage,
                    })}
                    isInvalid={!!errors["company_name"]}
                />
                {errors["company_name"]?.message && (
                    <Form.Control.Feedback type="invalid">
                        {errors["company_name"].message}
                    </Form.Control.Feedback>
                )}
            </Form.Group>

            <Form.Group as={Col} md={6} className="mb-4">
                <Form.Label>
                    Company Address <span className="required-label">*</span>
                </Form.Label>
                <Form.Control
                    type="text"
                    size="lg"
                    {...register("company_address", {
                        required: requiredFieldMessage,
                    })}
                    isInvalid={!!errors["company_address"]}
                />
                {errors["company_address"]?.message && (
                    <Form.Control.Feedback type="invalid">
                        {errors["company_address"].message}
                    </Form.Control.Feedback>
                )}
            </Form.Group>

            <Form.Group as={Col} md={4} className="mb-4">
                <Form.Label>
                    City <span className="required-label">*</span>
                </Form.Label>
                <Form.Control
                    type="text"
                    size="lg"
                    {...register("city", {
                        required: requiredFieldMessage,
                        validate: value =>
                            isValidName(value) || invalidFieldMessage,
                    })}
                    isInvalid={!!errors["city"]}
                />
                {errors["city"]?.message && (
                    <Form.Control.Feedback type="invalid">
                        {errors["city"].message}
                    </Form.Control.Feedback>
                )}
            </Form.Group>

            <Form.Group as={Col} md={4} className="mb-4">
                <Form.Label>
                    State <span className="required-label">*</span>
                </Form.Label>
                <Form.Select
                    size="lg"
                    isInvalid={!!errors["state"]}
                    defaultValue=""
                    {...register("state", {
                        required: requiredFieldMessage,
                    })}
                >
                    <option disabled value="">
                        {""}
                    </option>
                    {states.map(state => (
                        <option
                            value={state.abbreviation}
                            key={state.abbreviation}
                        >
                            {state.name}
                        </option>
                    ))}
                </Form.Select>
                {errors["state"]?.message && (
                    <Form.Control.Feedback type="invalid">
                        {errors["state"].message}
                    </Form.Control.Feedback>
                )}
            </Form.Group>

            <Form.Group as={Col} md={4} className="mb-4">
                <Form.Label>
                    Zip Code <span className="required-label">*</span>
                </Form.Label>
                <Form.Control
                    type="number"
                    size="lg"
                    {...register("zip_code", {
                        required: requiredFieldMessage,
                        maxLength: 5,
                        validate: value =>
                            isValidZipCode(value) || "Invalid ZIP Code format",
                    })}
                    isInvalid={!!errors["zip_code"]}
                />
                {errors["zip_code"]?.message && (
                    <Form.Control.Feedback type="invalid">
                        {errors["zip_code"].message}
                    </Form.Control.Feedback>
                )}
            </Form.Group>

            <Form.Group as={Col} md={4} className="mb-4">
                <Form.Label>
                    Company Website <span className="required-label">*</span>
                </Form.Label>
                <Form.Control
                    type="text"
                    size="lg"
                    {...register("company_website", {
                        required: requiredFieldMessage,
                        onBlur: e => addHttps(e.target.value),
                        validate: value =>
                            isWebsiteValid(value) ||
                            "Invalid website url format.",
                    })}
                    isInvalid={!!errors["company_website"]}
                />
                {errors["company_website"]?.message && (
                    <Form.Control.Feedback type="invalid">
                        {errors["company_website"].message}
                    </Form.Control.Feedback>
                )}
            </Form.Group>

            <Form.Group as={Col} md={4} className="mb-4">
                <Form.Label>
                    Email <span className="required-label">*</span>
                </Form.Label>
                <Form.Control
                    type="email"
                    size="lg"
                    {...register("company_email", {
                        required: requiredFieldMessage,
                        validate: value =>
                            isValidEmail(value) || "Invalid email format.",
                    })}
                    isInvalid={!!errors["company_email"]}
                />
                {errors["company_email"]?.message && (
                    <Form.Control.Feedback type="invalid">
                        {errors["company_email"].message}
                    </Form.Control.Feedback>
                )}
            </Form.Group>

            <Form.Group as={Col} md={4} className="mb-4">
                <Form.Label>
                    Phone <span className="required-label">*</span>
                </Form.Label>
                <Form.Control
                    type="tel"
                    maxLength="14"
                    size="lg"
                    {...register("company_phone", {
                        required: requiredFieldMessage,
                        onChange: e => updatePhoneNumber(e, "company_phone"),
                        validate: value =>
                            isValidPhoneNumber(value) ||
                            "Invalid phone number format.",
                    })}
                    isInvalid={!!errors["company_phone"]}
                />
                {errors["company_phone"]?.message && (
                    <Form.Control.Feedback type="invalid">
                        {errors["company_phone"].message}
                    </Form.Control.Feedback>
                )}
            </Form.Group>

            <Form.Group as={Col} md={6} className="d-flex flex-column mb-4">
                <Form.Label>
                    How long has the company been in operation?{" "}
                    <span className="required-label">*</span>
                </Form.Label>
                <Form.Control
                    type="text"
                    size="lg"
                    className="mt-auto"
                    {...register("company_operation_years", {
                        required: requiredFieldMessage,
                    })}
                    isInvalid={!!errors["company_operation_years"]}
                />
                {errors["company_operation_years"]?.message && (
                    <Form.Control.Feedback type="invalid">
                        {errors["company_operation_years"].message}
                    </Form.Control.Feedback>
                )}
            </Form.Group>

            <Form.Group as={Col} md={6} className="mb-4">
                <Form.Label>
                    How long has the company been actively providing temporary
                    healthcare staffing?{" "}
                    <span className="required-label">*</span>
                </Form.Label>
                <Form.Control
                    type="text"
                    size="lg"
                    className="mt-auto"
                    {...register("company_healthcare_staffing_years", {
                        required: requiredFieldMessage,
                    })}
                    isInvalid={!!errors["company_healthcare_staffing_years"]}
                />
                {errors["company_healthcare_staffing_years"]?.message && (
                    <Form.Control.Feedback type="invalid">
                        {errors["company_healthcare_staffing_years"].message}
                    </Form.Control.Feedback>
                )}
            </Form.Group>

            <Form.Group as={Col} xs={12} className="mb-4">
                <Form.Label>
                    Under which payroll category should the clinician(s)
                    provided to clients be classified?{" "}
                    <span className="required-label">*</span>
                </Form.Label>
                {payrollCategories?.map(category => (
                    <Form.Check
                        key={`radio-${category}`}
                        type="radio"
                        size="lg"
                        id={category}
                        value={category}
                        label={category}
                        {...register("payroll_category", {
                            required: requiredFieldMessage,
                        })}
                        isInvalid={!!errors["payroll_category"]}
                    />
                ))}

                <Form.Control.Feedback type="invalid" className="d-block">
                    {errors["payroll_category"]?.message}
                </Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} md={6} className="mb-4">
                <Form.Label>
                    Identify all beneficial owners (including silent
                    investors/partners) and officers{" "}
                    <span className="required-label">*</span>
                </Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    size="lg"
                    placeholder="Please include the name and the title of each beneficial owner. "
                    {...register("beneficial_owners", {
                        required: requiredFieldMessage,
                    })}
                    isInvalid={!!errors["beneficial_owners"]}
                />
                {errors["beneficial_owners"]?.message && (
                    <Form.Control.Feedback type="invalid">
                        {errors["beneficial_owners"].message}
                    </Form.Control.Feedback>
                )}
            </Form.Group>

            <Form.Group as={Col} md={6} className="mb-4">
                <Form.Label>
                    Are any owners or officers also a clinician that would be
                    staffing a client?
                </Form.Label>
                <Controller
                    name="clinician_owners"
                    control={control}
                    render={({ field: { onChange, value, name } }) => (
                        <RadioYesAndNoQuestionField
                            fieldName={name}
                            onChange={onChange}
                            value={value}
                        />
                    )}
                />
            </Form.Group>

            <Form.Group as={Col} md={6} className="mb-4">
                <Form.Label>Identify any parent companies</Form.Label>
                <Form.Control
                    type="text"
                    size="lg"
                    {...register("parent_companies")}
                />
            </Form.Group>

            <Form.Group as={Col} md={6} className="mb-4">
                <Form.Label>Identify any subsidiary companies</Form.Label>
                <Form.Control
                    type="text"
                    size="lg"
                    {...register("subsidiary_companies")}
                />
            </Form.Group>

            <Form.Group as={Col} md={6} className="mb-4">
                <Form.Label>Identify any affiliated companies</Form.Label>
                <Form.Control
                    type="text"
                    size="lg"
                    {...register("affiliated_companies")}
                />
            </Form.Group>

            <Form.Group as={Col} xs={12} className="mb-4">
                <Form.Label>
                    Does your company have an Employer of Record (EOR)
                    associated with the company? If yes, please provide the
                    appropriate information below.{" "}
                    <span className="required-label">*</span>
                </Form.Label>
                <Controller
                    name="has_eor"
                    control={control}
                    rules={radioYesAndNoValidation}
                    render={({
                        field: { onChange, value, name },
                        fieldState: { error },
                    }) => (
                        <RadioYesAndNoQuestionField
                            error={error}
                            fieldName={name}
                            onChange={onChange}
                            value={value}
                        />
                    )}
                />
            </Form.Group>

            {hasEOR && (
                <>
                    <Form.Group as={Col} md={6} className="mb-4">
                        <Form.Label>Employer of Record (EOR):</Form.Label>
                        <Form.Control
                            type="text"
                            size="lg"
                            {...register("eor")}
                        />
                    </Form.Group>

                    <SubVendorAdditionalInfoFields prefix="eor" />
                </>
            )}

            <Form.Group as={Col} md={12} className="mb-4">
                <Form.Label>
                    Does your company have a Professional Employer Organization
                    (PEO) associated with the company? If yes, please provide
                    the appropriate information below.{" "}
                    <span className="required-label">*</span>
                </Form.Label>

                <Controller
                    name="has_peo"
                    control={control}
                    rules={radioYesAndNoValidation}
                    render={({
                        field: { onChange, value, name },
                        fieldState: { error },
                    }) => (
                        <RadioYesAndNoQuestionField
                            error={error}
                            fieldName={name}
                            onChange={onChange}
                            value={value}
                        />
                    )}
                />
            </Form.Group>

            {hasPEO && (
                <>
                    <Form.Group as={Col} md={6} className="mb-4">
                        <Form.Label>
                            Professional Employer Organization (PEO)
                        </Form.Label>
                        <Form.Control
                            type="text"
                            size="lg"
                            {...register("peo")}
                        />
                    </Form.Group>

                    <SubVendorAdditionalInfoFields prefix="peo" />

                    <Form.Group as={Col} md={6} className="mb-4">
                        <Form.Label>
                            Which company is the W2 employer of the healthcare
                            professionals to be supplied?
                        </Form.Label>
                        <Form.Control
                            type="text"
                            size="lg"
                            {...register("peo_w2_employer_company")}
                        />
                    </Form.Group>
                </>
            )}

            <Form.Group as={Col} xs={12} className="mb-4">
                <Form.Label>
                    Does your company have any other back office organization
                    associated with the company? If yes, please provide the
                    appropriate information below.{" "}
                    <span className="required-label">*</span>
                </Form.Label>
                <Controller
                    name="has_back_office_organization"
                    control={control}
                    rules={radioYesAndNoValidation}
                    render={({
                        field: { onChange, value, name },
                        fieldState: { error },
                    }) => (
                        <RadioYesAndNoQuestionField
                            error={error}
                            fieldName={name}
                            onChange={onChange}
                            value={value}
                        />
                    )}
                />
            </Form.Group>

            {hasBackOffice && (
                <>
                    <Form.Group as={Col} xs={6} className="mb-4">
                        <Form.Label>Other</Form.Label>
                        <Form.Control
                            type="text"
                            size="lg"
                            {...register("back_office_other")}
                        />
                    </Form.Group>

                    <SubVendorAdditionalInfoFields prefix="back_office" />
                </>
            )}

            <Form.Group as={Col} xs={12} className="mb-4">
                <Form.Label>
                    Have any of the companies or owners been
                    excluded/removed/frozen by Shiftmed or CareerStaff
                    Unlimited? <span className="required-label">*</span>
                </Form.Label>
                <Controller
                    name="excluded_by_careerstaff"
                    control={control}
                    rules={radioYesAndNoValidation}
                    render={({
                        field: { onChange, value, name },
                        fieldState: { error },
                    }) => (
                        <RadioYesAndNoQuestionField
                            error={error}
                            fieldName={name}
                            onChange={onChange}
                            value={value}
                        />
                    )}
                />
            </Form.Group>

            {excludedByCareerstaff && (
                <Form.Group as={Col} xs={12} className="mb-4">
                    <Form.Label>If yes, please explain.</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        size="lg"
                        {...register("excluded_by_careerstaff_explanation")}
                    />
                </Form.Group>
            )}

            <Form.Group as={Col} xs={12} className="mb-4">
                <Form.Label>
                    Have any of the companies or owners been excluded by OIG or
                    any state regulators?{" "}
                    <span className="required-label">*</span>
                </Form.Label>
                <Controller
                    name="excluded_by_oig"
                    control={control}
                    rules={radioYesAndNoValidation}
                    render={({
                        field: { onChange, value, name },
                        fieldState: { error },
                    }) => (
                        <RadioYesAndNoQuestionField
                            error={error}
                            fieldName={name}
                            onChange={onChange}
                            value={value}
                            unregisterFields={"excluded_by_oig_explanation"}
                        />
                    )}
                />
            </Form.Group>

            {excludedByOIG && (
                <Form.Group as={Col} xs={12} className="mb-4">
                    <Form.Label>If yes, please explain.</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        size="lg"
                        {...register("excluded_by_oig_explanation")}
                    />
                </Form.Group>
            )}

            <Form.Group as={Col} xs={12} className="mb-4">
                <Form.Label>
                    Is this company categorized as a small business?{" "}
                    <span className="required-label">*</span>
                </Form.Label>
                <Controller
                    name="is_small_business"
                    control={control}
                    rules={radioYesAndNoValidation}
                    render={({
                        field: { onChange, value, name },
                        fieldState: { error },
                    }) => (
                        <RadioYesAndNoQuestionField
                            error={error}
                            fieldName={name}
                            onChange={onChange}
                            value={value}
                        />
                    )}
                />
            </Form.Group>

            {isSmallBusiness && (
                <Form.Group className="mb-4" as={Col} xs={12}>
                    <Form.Label>
                        If yes, please select all that apply.
                    </Form.Label>
                    {smallBusinessCategories.map(category => (
                        <Form.Check
                            key={`small_busines_${category}`}
                            type="checkbox"
                            size="lg"
                            id={`small_busines_${category}`}
                            value={category}
                            label={category}
                            {...register("small_business_categories", {
                                setValueAs: v => v || null,
                            })}
                        />
                    ))}
                </Form.Group>
            )}

            <Form.Group as={Col} xs={12} className="mb-4">
                <Form.Label>
                    Does your business staff locally to certain areas?{" "}
                    <span className="required-label">*</span>
                </Form.Label>
                <Controller
                    name="staffs_locally"
                    control={control}
                    rules={radioYesAndNoValidation}
                    render={({
                        field: { onChange, value, name },
                        fieldState: { error },
                    }) => (
                        <RadioYesAndNoQuestionField
                            error={error}
                            fieldName={name}
                            onChange={onChange}
                            value={value}
                        />
                    )}
                />
            </Form.Group>

            {staffsLocally && (
                <Form.Group className="mb-4" as={Col} xs={12}>
                    <Form.Label>
                        If yes, please select the local state(s) below.
                    </Form.Label>
                    <StatesCheckboxInput fieldName="local_staff_states" />
                </Form.Group>
            )}

            <Form.Group as={Col} xs={12} className="mb-4">
                <Form.Label>
                    Does your business staff Travel?{" "}
                    <span className="required-label">*</span>
                </Form.Label>
                <Controller
                    name="staffs_travel"
                    control={control}
                    rules={radioYesAndNoValidation}
                    render={({
                        field: { onChange, value, name },
                        fieldState: { error },
                    }) => (
                        <RadioYesAndNoQuestionField
                            error={error}
                            fieldName={name}
                            onChange={onChange}
                            value={value}
                        />
                    )}
                />
            </Form.Group>

            {staffsTravel && (
                <Form.Group className="mb-4" as={Col} xs={12}>
                    <Form.Label>
                        If yes, please select the local state(s) below.
                    </Form.Label>
                    <StatesCheckboxInput fieldName="travel_staff_states" />
                </Form.Group>
            )}

            <Form.Group as={Col} xs={12} className="mb-4">
                <Form.Label>
                    Does your business staff Per Diem?{" "}
                    <span className="required-label">*</span>
                </Form.Label>
                <Controller
                    name="staffs_per_diem"
                    control={control}
                    rules={radioYesAndNoValidation}
                    render={({
                        field: { onChange, value, name },
                        fieldState: { error },
                    }) => (
                        <RadioYesAndNoQuestionField
                            error={error}
                            fieldName={name}
                            onChange={onChange}
                            value={value}
                        />
                    )}
                />
            </Form.Group>

            {staffsPerDiem && (
                <Form.Group className="mb-4" as={Col} xs={12}>
                    <Form.Label>
                        If yes, please select the local state(s) below.
                    </Form.Label>
                    <StatesCheckboxInput fieldName="staffs_per_diem_states" />
                </Form.Group>
            )}

            <Form.Group as={Col} xs={12} className="mb-4">
                <Form.Label>
                    Does your business staff Contracts?{" "}
                    <span className="required-label">*</span>
                </Form.Label>
                <Controller
                    name="staffs_contracts"
                    control={control}
                    rules={radioYesAndNoValidation}
                    render={({
                        field: { onChange, value, name },
                        fieldState: { error },
                    }) => (
                        <RadioYesAndNoQuestionField
                            error={error}
                            fieldName={name}
                            onChange={onChange}
                            value={value}
                        />
                    )}
                />
            </Form.Group>

            {staffsContracts && (
                <Form.Group className="mb-4" as={Col} xs={12}>
                    <Form.Label>
                        If yes, please select the local state(s) below.
                    </Form.Label>
                    <StatesCheckboxInput fieldName="staffs_contracts_states" />
                </Form.Group>
            )}

            <Form.Group className="mb-4" as={Col} xs={12}>
                <Form.Label>
                    In which state(s) do you currently have an active pool of
                    healthcare professionals? Select all that apply.
                </Form.Label>
                <StatesCheckboxInput fieldName="active_healthcare_pool_states" />
            </Form.Group>

            <Form.Group className="mb-4" as={Col} xs={12}>
                <Form.Label>
                    What type of role or healthcare setting could your agency
                    currently fill with your active candidate pool? Please
                    select all that apply.{" "}
                    <span className="required-label">*</span>
                </Form.Label>
                {poolRoles.map(role => (
                    <Form.Check
                        key={`checkbox_${role}`}
                        type="checkbox"
                        size="lg"
                        id={role}
                        value={role}
                        label={role}
                        {...register("candidate_pool_roles", {
                            required: requiredFieldMessage,
                            setValueAs: v => v || null,
                        })}
                    />
                ))}
                {errors["candidate_pool_roles"]?.message && (
                    <Form.Control.Feedback type="invalid" className="d-block">
                        {errors["candidate_pool_roles"].message}
                    </Form.Control.Feedback>
                )}
            </Form.Group>

            {candidatePoolRoles?.some(role =>
                ["Nursing", "Allied"].includes(role)
            ) && (
                <Form.Group className="mb-4" as={Col} xs={12}>
                    <Form.Label>Please select all that apply?</Form.Label>
                    {nursingPoolRoles.map(nursingRole => (
                        <Form.Check
                            key={`nursing_role_${nursingRole}`}
                            type="checkbox"
                            size="lg"
                            id={`nursing_role_${nursingRole}`}
                            value={nursingRole}
                            label={nursingRole}
                            {...register("nursing_pool_roles", {
                                setValueAs: v => v || null,
                            })}
                        />
                    ))}
                </Form.Group>
            )}

            <Form.Group className="mb-4" as={Col} xs={12}>
                <Form.Label>
                    How did you hear about ShiftMed or CareerStaff?
                </Form.Label>
                {discoverySources.map(source => (
                    <Form.Check
                        key={`careerstaff_discovery_${source}`}
                        type="checkbox"
                        size="lg"
                        id={`careerstaff_discovery_${source}`}
                        value={source}
                        label={source}
                        {...register("careerstaff_discovery_sources", {
                            setValueAs: v => v || null,
                        })}
                    />
                ))}
            </Form.Group>

            {careerstaffDiscoverySources?.includes("Other") && (
                <Form.Group className="mb-4" as={Col} md={6}>
                    <Form.Label>
                        If you selected other, please specify:
                    </Form.Label>
                    <Form.Control
                        type="text"
                        size="lg"
                        {...register("other_discovery_source")}
                    />
                </Form.Group>
            )}
        </Row>
    );
};

export default SubVendorFirstFields;
